import { SkuMeta } from '@/components/SkuMeta';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useDebounced } from '@/api/useDebounced';
import { useApi } from '@/api/useApi';
import { ApiError } from '@/api/client';
import type { CreateManualOrderResult, ManualOrderShipTo, ManualOrderSku } from '@/api/types';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PermissionDenied } from '@/components/RequirePermission';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage, type TranslateFn } from '@/i18n/LanguageContext';
import { formatNumber } from '@/i18n/format';

/**
 * Ops create-order flow. Seller -> warehouse -> items (searched by code,
 * name or barcode, never a pasted id) -> recipient -> create. The server
 * (POST /api/v1/office/orders/manual) reuses the marketplace-ingestion
 * order shape and the ONE reservation path, so this screen only validates
 * what it can see (whole qty > 0, qty <= projected available, no duplicate
 * lines) and renders the server's own answer for everything else - the
 * server stays the authority (a race can still make stock run out between
 * the lookup and the click, and that comes back as ORDER.INSUFFICIENT_STOCK).
 */

type Line = {
  readonly skuId: string;
  readonly code: string;
  readonly name: string;
  readonly available: number;
  qty: string;
};

const WHOLE_UNITS = /^[1-9]\d*$/;

/** A per-form idempotency key: reused across retries of one submission, replaced after a success. */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Client-side line validation - the same rules the server enforces, shown before the round trip. */
export function lineProblem(t: TranslateFn, line: Line): string | null {
  const qty = line.qty.trim();
  if (!WHOLE_UNITS.test(qty)) return t('orderCreate.qtyInvalid', { sku: line.code });
  if (Number(qty) > line.available) {
    return t('orderCreate.qtyExceedsAvailable', { sku: line.code, available: String(line.available), requested: qty });
  }
  return null;
}

/**
 * Maps the create endpoint's domain errors to a sentence naming the SKU by
 * its CODE (resolved from this form's own lines when the server only had an
 * id to echo), never a uuid. Returns null for anything generic, which the
 * standard ErrorBanner then renders.
 */
export function describeCreateError(t: TranslateFn, err: unknown, lines: readonly Line[]): string | null {
  if (!(err instanceof ApiError)) return null;
  const p = err.params as Record<string, unknown>;
  const skuFromParams = (): string => {
    const byId = lines.find((l) => l.skuId === p.skuId);
    return byId ? byId.code : String(p.skuCode ?? '');
  };
  switch (err.code) {
    case 'ORDER.INSUFFICIENT_STOCK':
      return t('orderCreate.err.insufficientStock', {
        sku: skuFromParams(),
        available: String(p.available ?? '0'),
        requested: String(p.requested ?? ''),
      });
    case 'ORDER.SKU_NOT_OWNED_BY_SELLER':
      return t('orderCreate.err.skuNotOwned', { sku: skuFromParams() });
    case 'ORDER.INVALID_LINE_QTY':
      return t('orderCreate.qtyInvalid', { sku: skuFromParams() });
    case 'ORDER.LINES_REQUIRED':
      return t('orderCreate.err.linesRequired');
    case 'ORDER.NUMBER_TAKEN':
      return t('orderCreate.err.numberTaken', { number: String(p.number ?? '') });
    case 'ORDER.WAREHOUSE_OUT_OF_SCOPE':
      return t('orderCreate.err.warehouseOutOfScope');
    case 'ORDER.SELLER_ARCHIVED':
      return t('orderCreate.err.sellerArchived');
    case 'ORDER.WAREHOUSE_ARCHIVED':
      return t('orderCreate.err.warehouseArchived');
    case 'COMMON.NOT_FOUND':
      return t('orderCreate.err.notFound');
    default:
      return null;
  }
}

const EMPTY_SHIP_TO: Required<Pick<ManualOrderShipTo, 'name' | 'phone' | 'address' | 'city' | 'postalCode' | 'country'>> = {
  name: '',
  phone: '',
  address: '',
  city: '',
  postalCode: '',
  country: '',
};

export function OrderCreatePage(): JSX.Element {
  const { hasPermission } = useAuth();
  const { t } = useLanguage();
  if (!hasPermission('ORDERS.WRITE')) return <PermissionDenied reason={t('orderCreate.permissionDenied')} />;
  return <OrderCreateForm />;
}

function OrderCreateForm(): JSX.Element {
  const { t, locale } = useLanguage();
  const options = useApi(() => endpoints.manualOrderOptions(), []);

  const [ownerId, setOwnerId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [number, setNumber] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [fulfilment, setFulfilment] = useState<'' | 'STANDARD' | 'FBS'>('');
  const [shipTo, setShipTo] = useState(EMPTY_SHIP_TO);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<CreateManualOrderResult | null>(null);

  // A single-warehouse operator (the common case) should not have to pick it.
  useEffect(() => {
    if (options.status === 'success' && options.data.warehouses.length === 1 && !warehouseId) {
      setWarehouseId(options.data.warehouses[0]!.id);
    }
  }, [options.status, options.data, warehouseId]);

  const q = useDebounced(search.trim(), 250);
  const ready = ownerId !== '' && warehouseId !== '';
  const skus = useApi(
    () => (ready ? endpoints.manualOrderSkus({ ownerId, warehouseId, q: q || undefined, limit: 20 }) : Promise.resolve({ rows: [] as ManualOrderSku[] })),
    [ownerId, warehouseId, q, ready],
  );

  const problems = useMemo(() => new Map(lines.map((l) => [l.skuId, lineProblem(t, l)])), [lines, t]);
  const hasProblems = [...problems.values()].some((p) => p !== null);

  function resetLines(): void {
    setLines([]);
    setNotice(null);
    setError(null);
  }

  function addLine(sku: ManualOrderSku): void {
    setError(null);
    const existing = lines.find((l) => l.skuId === sku.sku_id);
    if (existing) {
      // Never a second line for the same SKU - bump the existing one instead.
      const current = WHOLE_UNITS.test(existing.qty.trim()) ? Number(existing.qty.trim()) : 0;
      setLines((prev) => prev.map((l) => (l.skuId === sku.sku_id ? { ...l, qty: String(current + 1) } : l)));
      setNotice(t('orderCreate.lineMerged', { sku: sku.code }));
      return;
    }
    setNotice(null);
    setLines((prev) => [...prev, { skuId: sku.sku_id, code: sku.code, name: sku.name, available: Number(sku.available), qty: '1' }]);
  }

  function setQty(skuId: string, qty: string): void {
    setError(null);
    setLines((prev) => prev.map((l) => (l.skuId === skuId ? { ...l, qty } : l)));
  }

  function removeLine(skuId: string): void {
    setError(null);
    setNotice(null);
    setLines((prev) => prev.filter((l) => l.skuId !== skuId));
  }

  async function submit(): Promise<void> {
    if (!ready || lines.length === 0 || hasProblems || !fulfilment) return;
    setSubmitting(true);
    setError(null);
    try {
      const recipient = Object.fromEntries(
        Object.entries(shipTo).filter(([, v]) => v.trim() !== '').map(([k, v]) => [k, v.trim()]),
      ) as ManualOrderShipTo;
      const result = await endpoints.createManualOrder({
        idempotencyKey,
        ownerId,
        warehouseId,
        number: number.trim() || null,
        dueAt: dueDate || null,
        fulfilmentModel: fulfilment || undefined,
        shipTo: Object.keys(recipient).length > 0 ? recipient : null,
        lines: lines.map((l) => ({ skuId: l.skuId, qty: l.qty.trim() })),
      });
      setCreated(result);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  function startAnother(): void {
    setCreated(null);
    setLines([]);
    setNotice(null);
    setNumber('');
    setDueDate('');
    setFulfilment('');
    setShipTo(EMPTY_SHIP_TO);
    setSearch('');
    setIdempotencyKey(newIdempotencyKey());
    skus.reload();
  }

  if (options.status === 'loading') return <LoadingState />;
  if (options.status === 'error') return <ErrorBanner error={options.error} onRetry={options.reload} />;

  if (created) {
    return (
      <div>
        <div className="page-header">
          <h1>{t('orderCreate.title')}</h1>
        </div>
        <div className="card" role="status">
          <p style={{ marginTop: 0 }}>
            <span className="badge ok">{t('orderCreate.success', { number: created.number })}</span>
          </p>
          <div className="form-actions">
            <Link className="button-link" to={`/shipments/${created.salesOrderId}`}>
              {t('orderCreate.openOrder', { number: created.number })}
            </Link>
            <button type="button" onClick={startAnother}>
              {t('orderCreate.createAnother')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const createError = error ? describeCreateError(t, error, lines) : null;
  const shipToField = (key: keyof typeof EMPTY_SHIP_TO, label: string, autoComplete: string) => (
    <div className="field-row">
      <label htmlFor={`oc-${key}`}>{label}</label>
      <input
        id={`oc-${key}`}
        value={shipTo[key]}
        autoComplete={autoComplete}
        onChange={(e) => setShipTo((prev) => ({ ...prev, [key]: e.target.value }))}
      />
    </div>
  );

  return (
    <div className="order-create">
      <div className="page-header">
        <div>
          <h1>{t('orderCreate.title')}</h1>
          <div className="sub">{t('orderCreate.subtitle')}</div>
        </div>
      </div>

      <div className="card">
        <div className="form-grid">
          <div className="field-row">
            <label htmlFor="oc-seller">{t('orderCreate.seller')}</label>
            <select
              id="oc-seller"
              value={ownerId}
              onChange={(e) => {
                setOwnerId(e.target.value);
                resetLines();
              }}
            >
              <option value="">{t('orderCreate.selectSeller')}</option>
              {options.data.sellers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.code})
                </option>
              ))}
            </select>
          </div>
          <div className="field-row">
            <label htmlFor="oc-warehouse">{t('orderCreate.warehouse')}</label>
            <select
              id="oc-warehouse"
              value={warehouseId}
              onChange={(e) => {
                setWarehouseId(e.target.value);
                resetLines();
              }}
            >
              <option value="">{t('orderCreate.selectWarehouse')}</option>
              {options.data.warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} ({w.code})
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="disabled-note" style={{ marginTop: 0 }}>{t('orderCreate.sellerChangeResets')}</p>
      </div>

      <div className="card">
        {!ready ? (
          <p className="sub" style={{ margin: 0 }}>{t('orderCreate.chooseSellerWarehouseFirst')}</p>
        ) : (
          <>
            <div className="field-row">
              <label htmlFor="oc-search">{t('orderCreate.findItem')}</label>
              <input id="oc-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
            </div>
            {skus.status === 'loading' && <LoadingState />}
            {skus.status === 'error' && <ErrorBanner error={skus.error} onRetry={skus.reload} />}
            {skus.status === 'success' &&
              (skus.data.rows.length === 0 ? (
                <p className="sub">{t('orderCreate.noItemsFound')}</p>
              ) : (
                <ul className="sku-results" aria-label={t('orderCreate.itemResults')}>
                  {skus.data.rows.map((s) => {
                    const available = Number(s.available);
                    return (
                      <li key={s.sku_id}>
                        <div>
                          <div>
                            <strong>{s.code}</strong> — {s.name}
                          </div>
                          <SkuMeta code={s.code} article={s.article} size={s.size} color={s.color} barcodes={s.barcodes} barcode={s.barcode} />
                          <div className="meta">{t('orderCreate.availableQty', { qty: formatNumber(s.available, locale) })}</div>
                        </div>
                        <button type="button" onClick={() => addLine(s)} disabled={available <= 0}>
                          {t('orderCreate.addItem', { sku: s.code })}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ))}
          </>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('orderCreate.lines')}</h2>
        {notice && <div className="notice" role="status">{notice}</div>}
        {lines.length === 0 ? (
          <p className="sub" style={{ margin: 0 }}>{t('orderCreate.noLines')}</p>
        ) : (
          <div className="table-scroll">
            <table className="lines-table">
              <thead>
                <tr>
                  <th>{t('orderCreate.colItem')}</th>
                  <th style={{ textAlign: 'right' }}>{t('orderCreate.colAvailable')}</th>
                  <th>{t('orderCreate.colQty')}</th>
                  <th>
                    <span className="sr-only">{t('common.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const problem = problems.get(l.skuId) ?? null;
                  const errId = `oc-qty-err-${l.skuId}`;
                  return (
                    <tr key={l.skuId}>
                      <td>
                        <strong>{l.code}</strong> — {l.name}
                      </td>
                      <td className="lines-available" data-label={t('orderCreate.colAvailable')} style={{ textAlign: 'right' }}>
                        {formatNumber(String(l.available), locale)}
                      </td>
                      <td className="lines-qty">
                        <input
                          className="qty-input"
                          inputMode="numeric"
                          aria-label={t('orderCreate.qtyFor', { sku: l.code })}
                          aria-invalid={problem ? 'true' : 'false'}
                          aria-describedby={problem ? errId : undefined}
                          value={l.qty}
                          onChange={(e) => setQty(l.skuId, e.target.value)}
                        />
                        {problem && (
                          <div className="field-error" id={errId}>
                            {problem}
                          </div>
                        )}
                      </td>
                      <td className="lines-remove">
                        <button type="button" className="danger" onClick={() => removeLine(l.skuId)} aria-label={t('orderCreate.removeLine', { sku: l.code })}>
                          {t('orderCreate.remove')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('orderCreate.orderDetails')}</h2>
        <div className="form-grid">
          <div className="field-row">
            <label htmlFor="oc-number">{t('orderCreate.orderNumber')}</label>
            <input id="oc-number" value={number} onChange={(e) => setNumber(e.target.value)} aria-describedby="oc-number-hint" />
            <span className="disabled-note" id="oc-number-hint">{t('orderCreate.orderNumberHint')}</span>
          </div>
          <div className="field-row">
            <label htmlFor="oc-fulfilment">{t('orderCreate.fulfilment')}</label>
            <select id="oc-fulfilment" required value={fulfilment} onChange={(e) => setFulfilment(e.target.value as '' | 'STANDARD' | 'FBS')} aria-describedby="oc-fulfilment-hint">
              <option value="">{t('orderCreate.fulfilmentSelect')}</option>
              <option value="STANDARD">{t('orderCreate.fulfilmentStandard')}</option>
              <option value="FBS">{t('orderCreate.fulfilmentFbs')}</option>
            </select>
            <span className="disabled-note" id="oc-fulfilment-hint">{t('orderCreate.fulfilmentHint')}</span>
          </div>
          <div className="field-row">
            <label htmlFor="oc-due">{t('orderCreate.dueDate')}</label>
            <input id="oc-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
        </div>
        <h2 style={{ fontSize: 14 }}>{t('orderCreate.recipient')}</h2>
        <div className="form-grid">
          {shipToField('name', t('orderCreate.recipientName'), 'name')}
          {shipToField('phone', t('orderCreate.phone'), 'tel')}
          {shipToField('address', t('orderCreate.address'), 'street-address')}
          {shipToField('city', t('orderCreate.city'), 'address-level2')}
          {shipToField('postalCode', t('orderCreate.postalCode'), 'postal-code')}
          {shipToField('country', t('orderCreate.country'), 'country-name')}
        </div>
      </div>

      <div className="card">
        {createError ? (
          <div className="error-banner" role="alert">
            <div>{createError}</div>
            {error instanceof ApiError && <div className="rid">request_id: {error.requestId}</div>}
          </div>
        ) : (
          Boolean(error) && <ErrorBanner error={error} />
        )}
        {hasProblems && <p className="field-error" style={{ marginTop: 0 }}>{t('orderCreate.fixErrors')}</p>}
        <div className="form-actions" style={{ marginTop: 0 }}>
          <button
            type="button"
            className="primary"
            disabled={submitting || !ready || lines.length === 0 || hasProblems || !fulfilment}
            onClick={() => void submit()}
          >
            {submitting ? t('orderCreate.submitting') : t('orderCreate.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
